import { useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { ApiError, explain } from '@/shared/api/problem'
import { Modal } from '@/shared/ui/Modal'
import { ErrorStrip } from '@/shared/ui/states'
import { EntityPicker } from '@/shared/pickers/EntityPicker'
import { UnitPicker } from './UnitPicker'
import { createCandidacy, updateCandidacy, type Candidacy, type CandidacyInput } from './api'
import { SEAT_LEVEL, SEAT_ORDER, label } from './vocab'
import { DatePicker } from '@/components/erp/date-picker'

/**
 * Nominate a candidate to a seat, by hand.
 *
 * ## The unit picker follows the seat
 *
 * A President contests the country, an MCA a ward. Choosing the seat first and filtering the
 * unit picker to that level is what stops the commonest mistake — picking a county for an MP
 * — before the server has to refuse it. **The server is still the authority**: it returns a
 * problem carrying `requiredLevel`, and that refusal is shown verbatim rather than
 * pre-empted, because a client-side rule that disagrees with the server is worse than none.
 *
 * ## Party is captured here, not derived
 *
 * It defaults to the candidate's current party as a convenience and is then stored on the
 * candidacy — the party as at nomination, which is what the ballot said. That is why the
 * field is editable even though the person already has one.
 */
export function CandidacyForm({
  open, electionId, existing, onClose, onSaved,
}: {
  open: boolean
  electionId: string
  /** Present when amending. A certified candidacy is refused by the server. */
  existing?: Candidacy | null
  onClose: () => void
  onSaved: () => void | Promise<void>
}) {
  const [candidateId, setCandidateId] = useState<string | null>(null)
  const [seatTypeCode, setSeatTypeCode] = useState('')
  const [unitId, setUnitId] = useState<string | null>(null)
  const [partyId, setPartyId] = useState<string | null>(null)
  const [coalitionId, setCoalitionId] = useState<string | null>(null)
  const [independent, setIndependent] = useState(false)
  const [ballotName, setBallotName] = useState('')
  const [ballotNumber, setBallotNumber] = useState('')
  const [nominatedOn, setNominatedOn] = useState('')
  const [notes, setNotes] = useState('')

  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fields, setFields] = useState<Record<string, string>>({})

  // Seed from the row being amended, and reset between opens. `existing` and `open` are both
  // listed: without them a second open would carry the first row's values.
  useEffect(() => {
    if (!open) return
    setCandidateId(existing?.candidateId ?? null)
    setSeatTypeCode(existing?.seatTypeCode ?? '')
    setUnitId(existing?.unitId ?? null)
    setPartyId(existing?.partyId ?? null)
    setCoalitionId(existing?.coalitionId ?? null)
    setIndependent(existing?.independent ?? false)
    setBallotName(existing?.ballotName ?? '')
    setBallotNumber(existing?.ballotNumber ?? '')
    setNominatedOn(existing?.nominatedOn ?? '')
    setNotes(existing?.notes ?? '')
    setError(null)
    setFields({})
  }, [open, existing])

  // Changing the seat invalidates the unit: an MP's constituency is not a governor's county.
  // Silently keeping it would submit a mismatch the server then refuses.
  useEffect(() => {
    if (!existing) setUnitId(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seatTypeCode])

  const requiredLevel = SEAT_LEVEL[seatTypeCode] ?? null
  const canSave = Boolean(candidateId && seatTypeCode && unitId)

  const save = async () => {
    if (!candidateId || !unitId) return
    setBusy(true)
    setError(null)
    setFields({})
    const body: CandidacyInput = {
      electionId,
      candidateId,
      seatTypeCode,
      unitId,
      partyId: independent ? null : partyId,
      coalitionId,
      independent,
      ballotName: ballotName.trim() || null,
      ballotNumber: ballotNumber.trim() || null,
      nominatedOn: nominatedOn || null,
      notes: notes.trim() || null,
    }
    try {
      if (existing) await updateCandidacy(existing.id, body)
      else await createCandidacy(body)
      await onSaved()
    } catch (err) {
      // The seat/level refusal carries `requiredLevel` in its problem detail — the useful
      // response is the sentence, which names both the level wanted and the one given.
      setError(explain(err))
      if (err instanceof ApiError) setFields(err.fieldErrors())
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal open={open} onClose={onClose} width="lg"
      title={existing ? 'Amend the nomination' : 'Nominate a candidate'}
      description="A candidacy is this person, contesting this seat, at this place, in this election."
      footer={(
        <>
          <button type="button" className="erp-btn h-9 px-3 text-erp-sm" onClick={onClose}>
            Cancel
          </button>
          <button type="button" disabled={busy || !canSave}
            onClick={() => void save()}
            className="erp-btn-primary h-9 px-3 text-erp-sm inline-flex items-center gap-2">
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {existing ? 'Save' : 'Nominate'}
          </button>
        </>
      )}>
      <div className="space-y-4">
        <ErrorStrip message={error} className="rounded-md border" />

        <Field label="Candidate" error={fields.candidateId}
          hint="From the register of people. Add them there first if they are not listed.">
          <EntityPicker source="candidate" value={candidateId} onChange={setCandidateId}
            placeholder="Search the candidate register…" />
        </Field>

        <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
          <Field label="Seat" error={fields.seatTypeCode}
            hint={requiredLevel ? `Contested at ${label(requiredLevel).toLowerCase()} level.` : undefined}>
            <select className="erp-input h-9 w-full" value={seatTypeCode}
              onChange={(e) => setSeatTypeCode(e.target.value)}>
              <option value="">Choose a seat…</option>
              {SEAT_ORDER.map((s) => <option key={s} value={s}>{label(s)}</option>)}
            </select>
          </Field>

          <Field label="Where" error={fields.unitId}
            hint={seatTypeCode
              ? undefined
              : 'Choose a seat first — it decides which places are valid.'}>
            <UnitPicker level={requiredLevel} value={unitId} onPick={setUnitId}
              disabled={!seatTypeCode} />
          </Field>
        </div>

        <div className="pt-2 border-t border-border">
          <div className="text-erp-xs font-medium uppercase tracking-wide text-muted-foreground mb-3">
            The ticket, as it will appear on the ballot
          </div>

          <label className="inline-flex items-center gap-2 text-erp-sm mb-3">
            <input type="checkbox" checked={independent}
              onChange={(e) => {
                setIndependent(e.target.checked)
                // Both directions, so the two controls can never contradict: the server
                // refuses "independent with a party" and so does the database.
                if (e.target.checked) setPartyId(null)
              }} />
            Standing as an independent
          </label>

          <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
            <Field label="Party" error={fields.partyId}
              hint="The party AS AT NOMINATION — what the paper says. Frozen once saved.">
              {independent ? (
                <p className="h-9 flex items-center text-erp-sm text-muted-foreground">
                  None — standing independently
                </p>
              ) : (
                <EntityPicker source="party" value={partyId} onChange={setPartyId}
                  placeholder="Defaults to the candidate's party…" />
              )}
            </Field>

            <Field label="Coalition" error={fields.coalitionId}
              hint="Usually derived from the party. Set it for an independent who sits in one.">
              <EntityPicker source="coalition" value={coalitionId} onChange={setCoalitionId}
                placeholder="Optional…" />
            </Field>
          </div>

          <div className="grid gap-4 mt-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
            <Field label="Name on the ballot" error={fields.ballotName}
              hint="Leave empty to use the register's name. Fill it where the paper differs.">
              <input className="erp-input h-9 w-full" maxLength={200} value={ballotName}
                onChange={(e) => setBallotName(e.target.value)} />
            </Field>
            <Field label="Ballot number" error={fields.ballotNumber}>
              <input className="erp-input h-9 w-full" maxLength={16} value={ballotNumber}
                onChange={(e) => setBallotNumber(e.target.value)} />
            </Field>
            <Field label="Nominated on" error={fields.nominatedOn}>
              <DatePicker value={nominatedOn} onChange={setNominatedOn} className="h-9 w-full" />
            </Field>
          </div>

          <Field label="Notes" className="mt-4">
            <textarea rows={2} className="erp-input w-full py-2" value={notes}
              onChange={(e) => setNotes(e.target.value)} />
          </Field>
        </div>

        {!existing && (
          <p className="text-erp-xs text-muted-foreground border-l-2 border-border pl-3">
            This records the nomination as <strong>prospective</strong>. Clearing it for the
            ballot and issuing a certificate are separate, separately-authorised steps.
          </p>
        )}
      </div>
    </Modal>
  )
}

function Field({
  label: text, hint, error, children, className,
}: {
  label: string
  hint?: string
  error?: string
  children: React.ReactNode
  className?: string
}) {
  return (
    <div className={className}>
      <span className="block text-erp-xs font-medium text-muted-foreground mb-1">{text}</span>
      {children}
      {hint && !error && <p className="mt-1 text-erp-xs text-muted-foreground/80">{hint}</p>}
      {error && <p className="mt-1 text-erp-xs text-red-700 dark:text-red-400">{error}</p>}
    </div>
  )
}
